<template>
  <v-container fluid>
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card dark outlined>
          <v-card>
            <v-card-title class="text-h6">
              {{ Action === "update" ? "Edit Device" : "Device Management" }}
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text>
              <v-container>
                <!-- CSV Import Section -->
                <v-row v-if="Action !== 'update'">
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title class="text-subtitle-1">
                        <v-icon left>mdi-file-csv</v-icon>
                        Import Devices from CSV
                      </v-card-title>
                      <v-card-text>
                        <v-file-input
                          v-model="csvFile"
                          accept=".csv"
                          label="Select CSV file"
                          prepend-icon="mdi-file-upload"
                          @change="handleFileUpload"
                          :rules="[
                            (v) =>
                              !v ||
                              v.size < 2000000 ||
                              'File size should be less than 2 MB',
                          ]"
                        ></v-file-input>

                        <v-btn
                          color="secondary"
                          small
                          @click="downloadTemplate"
                          class="mb-3"
                        >
                          <v-icon left small>mdi-download</v-icon>
                          Download CSV Template
                        </v-btn>

                        <v-alert v-if="csvError" type="error" dense>
                          {{ csvError }}
                        </v-alert>

                        <v-alert v-if="csvSuccess" type="success" dense>
                          {{ csvSuccess }}
                        </v-alert>

                        <v-btn
                          v-if="importedDevices.length > 0"
                          color="primary"
                          @click="clearImportedDevices"
                          class="mt-2"
                        >
                          Clear Imported Devices
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Imported Devices Section -->
                <v-row v-if="importedDevices.length > 0">
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title class="text-subtitle-1">
                        <v-icon left>mdi-devices</v-icon>
                        Imported Devices ({{ importedDevices.length }})
                      </v-card-title>

                      <!-- Summary Statistics -->
                      <v-card-text class="pb-0">
                        <v-row>
                          <v-col cols="12" sm="6" md="3">
                            <v-card outlined class="text-center pa-2">
                              <div class="text-h6">
                                {{ importedDevices.length }}
                              </div>
                              <div class="text-caption">Total Devices</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <v-card outlined class="text-center pa-2">
                              <div class="text-h6 text-success">
                                {{ readyToSaveCount }}
                              </div>
                              <div class="text-caption">Ready to Save</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <v-card outlined class="text-center pa-2">
                              <div class="text-h6 text-warning">
                                {{ mappingWarningsCount }}
                              </div>
                              <div class="text-caption">Mapping Warnings</div>
                            </v-card>
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <v-card outlined class="text-center pa-2">
                              <div class="text-h6 text-error">
                                {{ invalidCount }}
                              </div>
                              <div class="text-caption">Invalid Data</div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </v-card-text>

                      <v-card-text>
                        <admin-DeviceRow
                          v-for="(device, index) in importedDevices"
                          :key="index"
                          :device="device"
                          :index="index"
                          @update="updateImportedDevice"
                          @remove="removeImportedDevice"
                        />

                        <!-- Bulk Actions -->
                        <v-row class="mt-4">
                          <v-col cols="12" class="text-center">
                            <v-btn
                              v-if="readyToSaveCount > 0"
                              color="success"
                              @click="saveAll"
                              :disabled="readyToSaveCount === 0"
                            >
                              <v-icon left>mdi-content-save</v-icon>
                              Save {{ readyToSaveCount }} Valid Devices
                            </v-btn>

                            <v-alert
                              v-if="mappingWarningsCount > 0"
                              type="warning"
                              dense
                              class="mt-3"
                            >
                              <v-icon left small>mdi-alert</v-icon>
                              {{ mappingWarningsCount }} device(s) have mapping
                              warnings but can still be saved
                            </v-alert>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Single Device Form -->
                <v-row v-if="importedDevices.length === 0">
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title class="text-subtitle-1">
                        <v-icon left>mdi-device</v-icon>
                        {{
                          Action === "update"
                            ? "Edit Device"
                            : "Add Single Device"
                        }}
                      </v-card-title>
                      <v-card-text>
                        <v-form ref="form" v-model="valid" lazy-validation>
                          <v-row>
                            <v-col cols="12">
                              <v-text-field
                                v-model="eventItem.DeviceId"
                                label="Device ID"
                                :rules="[(v) => !!v || 'Device ID is required']"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="12">
                              <v-text-field
                                v-model="eventItem.MerchantId"
                                label="CAIC"
                                type="number"
                                :rules="[
                                  (v) => !!v || 'CAIC is required',
                                ]"
                              ></v-text-field>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col cols="12">
                              <v-combobox
                                v-model="eventItem.Authorised"
                                :items="authorisedTypes"
                                item-text="name"
                                :return-object="false"
                                label="Device Authorised"
                                required
                              ></v-combobox>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col>
                              <br />
                              <p>
                                If you choose to enter any of the below fields,
                                you must complete all of them:
                              </p>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col cols="12">
                              <v-autocomplete
                                :items="allStallHolders"
                                v-model="selectedStallholder"
                                item-text="StallholderName"
                                return-object
                                clearable
                                filled
                                dense
                                outlined
                                hide-details
                                label="Stallholder"
                                @change="changeStallholder"
                              >
                                <template v-slot:no-data>
                                  <v-list-item>
                                    <v-list-item-title>
                                      <v-icon small color="red lighten-1">
                                        mdi-information
                                      </v-icon>
                                      No Stallholders found
                                    </v-list-item-title>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col cols="12">
                              <v-autocomplete
                                :items="currentStallholderStalls"
                                v-model="selectedStall"
                                item-text="StallName"
                                return-object
                                clearable
                                filled
                                dense
                                outlined
                                hide-details
                                label="Stall"
                                @change="changeStall"
                              >
                                <template v-slot:no-data>
                                  <v-list-item>
                                    <v-list-item-title>
                                      <v-icon small color="red lighten-1">
                                        mdi-information
                                      </v-icon>
                                      No Stalls found
                                    </v-list-item-title>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col cols="12">
                              <v-autocomplete
                                :items="currentStallTerminals"
                                v-model="selectedTerminal"
                                item-text="TerminalNumber"
                                return-object
                                clearable
                                filled
                                dense
                                outlined
                                hide-details
                                label="Terminal"
                              >
                                <template v-slot:no-data>
                                  <v-list-item>
                                    <v-list-item-title>
                                      <v-icon small color="red lighten-1">
                                        mdi-information
                                      </v-icon>
                                      No Terminals found
                                    </v-list-item-title>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col cols="12">
                              <v-autocomplete
                                :items="currentStallholderUsers"
                                v-model="selectedUser"
                                item-text="TerminalUserName"
                                return-object
                                clearable
                                filled
                                dense
                                outlined
                                hide-details
                                label="User"
                              >
                                <template v-slot:no-data>
                                  <v-list-item>
                                    <v-list-item-title>
                                      <v-icon small color="red lighten-1">
                                        mdi-information
                                      </v-icon>
                                      No users found for this stallholder
                                    </v-list-item-title>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                          </v-row>
                        </v-form>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="clickCancel"
                >Cancel
              </v-btn>
              <v-btn color="blue darken-1" text @click="save">Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import "izitoast/dist/css/iziToast.min.css";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import { mapState, mapActions, mapMutations } from "vuex";
import DeviceRow from "@/components/admin/DeviceRow.vue";
import { hasWrite } from "@/utils/permissions";

export default {
  components: {
    "admin-DeviceRow": DeviceRow,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    deviceData: {
      type: Object,
      default: null,
    },
    stallholderData: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    eventItem: {
      Authorised: "Yes",
    },
    /** DeviceId when edit form was opened — required to rename without duplicating. */
    originalDeviceId: null,

    editedItem: {
      Stallholder: "",
      Stall: "",
      Terminal: "",
    },

    Stallholder: {},

    selectedStallholder: {},
    selectedStall: {},
    selectedTerminal: {},
    selectedUser: null,
    stalls: [],
    terminals: [],
    terminalUsers: [],

    authorisedTypes: [
      { id: "Yes", name: "Yes" },
      { id: "No", name: "No" },
    ],

    valid: true,

    Action: "create",
    /** Skip clearing Stall/Terminal/User while edit form is being populated */
    hydratingEdit: false,
    editHydrated: false,

    // CSV Import related data
    csvFile: null,
    csvError: "",
    csvSuccess: "",
    importedDevices: [],
  }),

  computed: {
    ...mapState("filter", [
      "globalEventFilter",
      "allStallHolders",
      "allTerminalUsers",
      "IsAdmin",
    ]),
    ...mapState("admin", ["currentStallholder"]),

    eventPermission() {
      return this.globalEventFilter?.Permission || "None";
    },

    // Mutate only with event-level Write or TSG admin — not stallholder Write
    canManageDevices() {
      return this.IsAdmin || hasWrite(this.eventPermission);
    },

    // Computed property that automatically updates when allStallHolders changes
    currentStallholderStalls() {
      if (
        this.selectedStallholder &&
        this.selectedStallholder.StallholderId &&
        this.allStallHolders
      ) {
        const freshStallholder = this.allStallHolders.find(
          (sh) => sh.StallholderId === this.selectedStallholder.StallholderId
        );
        return freshStallholder?.Stalls || [];
      }
      return [];
    },

    // Computed property for current stall's terminals
    currentStallTerminals() {
      if (
        this.selectedStall &&
        this.selectedStall.StallId &&
        this.currentStallholderStalls.length > 0
      ) {
        const freshStall = this.currentStallholderStalls.find(
          (s) => s.StallId === this.selectedStall.StallId
        );
        return (
          freshStall?.Terminals?.sort(
            (a, b) => parseInt(a.TerminalNumber) - parseInt(b.TerminalNumber)
          ) || []
        );
      }
      return [];
    },

    // Computed property for current stallholder's terminal users
    currentStallholderUsers() {
      if (
        this.selectedStallholder &&
        this.selectedStallholder.StallholderId &&
        this.allStallHolders
      ) {
        const freshStallholder = this.allStallHolders.find(
          (sh) => sh.StallholderId === this.selectedStallholder.StallholderId
        );
        return freshStallholder?.TerminalUsers || [];
      }
      return [];
    },

    fullyMappedCount() {
      return this.importedDevices.filter(
        (device) =>
          device.DeviceId &&
          device.MerchantId &&
          device.hasValidMerchantId &&
          // Either no CSV mapping data was provided, OR all provided CSV data was successfully mapped
          ((!device.hasCsvStallholder &&
            !device.hasCsvStall &&
            !device.hasCsvTerminal &&
            !device.hasCsvUser) ||
            ((!device.hasCsvStallholder || device.StallholderId) &&
              (!device.hasCsvStall || device.StallId) &&
              (!device.hasCsvTerminal || device.TerminalId) &&
              (!device.hasCsvUser || device.UserId)))
      ).length;
    },

    mappingWarningsCount() {
      return this.importedDevices.filter(
        (device) =>
          device.DeviceId &&
          device.MerchantId &&
          device.hasValidMerchantId &&
          // Has CSV mapping data but some mappings failed
          ((device.hasCsvStallholder && !device.StallholderId) ||
            (device.hasCsvStall && !device.StallId) ||
            (device.hasCsvTerminal && !device.TerminalId) ||
            (device.hasCsvUser && !device.UserId))
      ).length;
    },

    readyToSaveCount() {
      return this.importedDevices.filter(
        (device) =>
          device.DeviceId && device.MerchantId && device.hasValidMerchantId
      ).length;
    },

    invalidCount() {
      return this.importedDevices.filter(
        (device) =>
          !device.DeviceId || !device.MerchantId || !device.hasValidMerchantId
      ).length;
    },
  },

  created() {
    if (!this.canManageDevices) {
      iziToast.warning({
        title: "Access Denied",
        message:
          "You do not have permission to add or edit devices for this event.",
        position: "topRight",
      });
      this.setCurrentEventManagementTab("Devices");
      this.$router.push({ name: "EventManagement" });
      return;
    }

    if (this.id && this.id !== "0") {
      this.Action = "update";
      if (this.deviceData) {
        this.originalDeviceId = this.deviceData.DeviceId;
        this.eventItem.DeviceId = this.deviceData.DeviceId;
        this.eventItem.MerchantId = this.deviceData.MerchantId;
        this.eventItem.Authorised = this.deviceData.Authorised || "Yes";
        this.hydrateEditAssignments();
      } else {
        console.warn("Device data not provided via props for edit mode");
      }
    } else {
      this.Action = "create";
      this.originalDeviceId = null;
      this.selectedStallholder = null;
    }
  },

  mounted() {
    // Disable event selector while on this page
    this.setEventSelectorDisabled(true);

    this.$nextTick(() => {
      if (this.selectedStallholder && this.selectedStallholder.StallholderId) {
        this.refreshTerminalUsers();
      }
    });
  },

  beforeDestroy() {
    // Re-enable event selector when leaving this page
    this.setEventSelectorDisabled(false);

    // Clean up any old localStorage data that might still exist
    localStorage.removeItem("setDeviceObj");
    localStorage.removeItem("setStallholderObj");
  },

  watch: {
    Stallholder: function (val) {
      if (val && val.StallholderId) {
        console.log(this.allStallHolders);
      }
    },

    selectedStallholder: function () {
      // Do not wipe Stall/Terminal/User while loading an existing device
      if (this.hydratingEdit) return;
      this.selectedUser = null;
      this.selectedStall = null;
      this.selectedTerminal = null;
    },

    selectedStall: function (val, oldVal) {
      if (this.hydratingEdit) return;
      // Only clear terminal when the stall actually changes (user action)
      const prevId = oldVal && oldVal.StallId;
      const nextId = val && val.StallId;
      if (prevId && prevId !== nextId) {
        this.selectedTerminal = null;
      } else if (!nextId) {
        this.selectedTerminal = null;
      }
    },

    // Stallholders / users may load after navigation — hydrate when ready
    allStallHolders: {
      handler(list) {
        if (
          this.Action === "update" &&
          this.deviceData &&
          list &&
          list.length > 0 &&
          !this.editHydrated
        ) {
          this.hydrateEditAssignments();
        }
      },
    },

    allTerminalUsers: {
      handler(list) {
        if (
          this.Action === "update" &&
          this.deviceData &&
          list &&
          list.length > 0 &&
          this.editHydrated &&
          !this.selectedUser
        ) {
          this.hydrateEditUser();
        }
      },
    },

    selectedUser: function () {},
  },

  methods: {
    ...mapActions("admin", ["postDevices"]),
    ...mapMutations("admin", [
      "setEventSelectorDisabled",
      "setCurrentEventManagementTab",
    ]),

    /** Resolve Stall / Terminal / User from deviceData into autocomplete items. */
    hydrateEditAssignments() {
      const eventItem = this.deviceData;
      if (!eventItem || !this.allStallHolders || !this.allStallHolders.length) {
        return;
      }

      this.hydratingEdit = true;
      try {
        if (eventItem.StallholderId) {
          const stallholder = this.allStallHolders.find(
            (sh) => sh.StallholderId === eventItem.StallholderId
          );
          if (stallholder) {
            this.selectedStallholder = stallholder;

            if (eventItem.StallId) {
              const stalls = stallholder.Stalls || [];
              const stall = stalls.find((s) => s.StallId === eventItem.StallId);
              if (stall) {
                this.selectedStall = stall;

                if (eventItem.TerminalId) {
                  const terminals = stall.Terminals || [];
                  const terminal = terminals.find(
                    (t) => t.TerminalId === eventItem.TerminalId
                  );
                  if (terminal) {
                    this.selectedTerminal = terminal;
                  }
                }
              }
            }
          }
        }

        this.hydrateEditUser();
        this.editHydrated = true;
      } finally {
        this.$nextTick(() => {
          this.hydratingEdit = false;
        });
      }
    },

    hydrateEditUser() {
      const eventItem = this.deviceData;
      if (!eventItem) return;
      const userId =
        eventItem.TerminalUserId || eventItem.UserId || null;
      if (!userId) return;

      const users = this.allTerminalUsers || [];
      const user =
        users.find((u) => u.TerminalUserId === userId) ||
        this.currentStallholderUsers.find(
          (u) => u.TerminalUserId === userId
        );
      if (user) {
        this.selectedUser = user;
      }
    },

    // CSV Import methods
    handleFileUpload(file) {
      if (!file) {
        this.csvError = "";
        this.csvSuccess = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvContent = e.target.result;
          const devices = this.parseCSV(csvContent);

          if (devices.length > 0) {
            this.importedDevices = devices;
            this.csvSuccess = `Successfully imported ${devices.length} devices from CSV`;
            this.csvError = "";
          } else {
            this.csvError = "No valid devices found in CSV file";
            this.csvSuccess = "";
          }
        } catch (error) {
          this.csvError = `Error parsing CSV: ${error.message}`;
          this.csvSuccess = "";
        }
      };

      reader.readAsText(file);
    },

    parseCSV(csvContent) {
      const lines = csvContent.split("\n");
      const headers = lines[0].split(",").map((h) => h.trim());

      // Expected headers (CAIC replaces MerchantId label; accept either for import)
      const expectedHeaders = [
        "DeviceID",
        "Authorised",
        "Stallholder",
        "Stall",
        "Terminal",
        "User",
      ];

      // Validate headers
      const missingHeaders = expectedHeaders.filter(
        (h) => !headers.includes(h)
      );
      if (missingHeaders.length > 0) {
        throw new Error(
          `Missing required headers: ${missingHeaders.join(", ")}`
        );
      }
      const caicHeader =
        headers.includes("CAIC")
          ? "CAIC"
          : headers.includes("MerchantId")
            ? "MerchantId"
            : null;
      if (!caicHeader) {
        throw new Error("Missing required headers: CAIC");
      }

      const devices = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = this.parseCSVLine(line);
        if (values.length !== headers.length) continue;

        const merchantIdValue = values[headers.indexOf(caicHeader)];
        const stallholderValue = values[headers.indexOf("Stallholder")];
        const stallValue = values[headers.indexOf("Stall")];
        const terminalValue = values[headers.indexOf("Terminal")];
        const userValue = values[headers.indexOf("User")];

        const device = {
          DeviceId: values[headers.indexOf("DeviceID")],
          MerchantId: merchantIdValue,
          Authorised: values[headers.indexOf("Authorised")],
          Stallholder: stallholderValue,
          Stall: stallValue,
          Terminal: terminalValue,
          User: userValue,
          EventId: this.globalEventFilter.EventId,
          // Add validation flags
          hasValidMerchantId: this.isValidMerchantId(merchantIdValue),
          originalMerchantId: merchantIdValue, // Keep original value for display
          // Track whether CSV data was provided for mapping
          hasCsvStallholder: !!(stallholderValue && stallholderValue.trim()),
          hasCsvStall: !!(stallValue && stallValue.trim()),
          hasCsvTerminal: !!(terminalValue && terminalValue.trim()),
          hasCsvUser: !!(userValue && userValue.trim()),
        };

        // Add all devices with DeviceId, regardless of MerchantId validity
        if (device.DeviceId) {
          devices.push(device);
        }
      }

      return devices;
    },

    parseCSVLine(line) {
      const values = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }

      values.push(current.trim());
      return values;
    },

    isValidMerchantId(value) {
      // Check if the value is a valid number (including integer and decimal)
      if (!value || value.toString().trim() === "") {
        return false;
      }

      const trimmedValue = value.toString().trim();
      const numericValue = Number(trimmedValue);

      // Check if it's a valid number and not NaN
      return (
        !isNaN(numericValue) && isFinite(numericValue) && trimmedValue !== ""
      );
    },

    updateImportedDevice(device, index) {
      this.$set(this.importedDevices, index, device);
    },

    removeImportedDevice(index) {
      this.importedDevices.splice(index, 1);
    },

    clearImportedDevices() {
      this.importedDevices = [];
      this.csvFile = null;
      this.csvError = "";
      this.csvSuccess = "";
    },

    changeStallholder: function (obj) {
      this.editedItem.Stallholder = obj;
      this.Stallholder = obj;

      // Reset selected user when stallholder changes
      this.selectedUser = null;
    },

    // Helper method to refresh terminal users for current stallholder (now uses computed property)
    refreshTerminalUsers() {
      console.log(
        "Terminal users automatically refreshed via computed property:",
        this.currentStallholderUsers.length
      );
    },

    changeStall: function (obj) {
      this.editedItem.Stall = obj;
      this.selectedStall = obj;
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.eventItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    save() {
      if (!this.canManageDevices) {
        iziToast.warning({
          title: "Access Denied",
          message:
            "You do not have permission to add or edit devices for this event.",
          position: "topRight",
        });
        return;
      }

      let devicesToSubmit = [];

      // If we have imported devices, use those
      if (this.importedDevices.length > 0) {
        devicesToSubmit = this.importedDevices;
      } else {
        // Single device form. Cleared autocomplete (null) must not fall back
        // to loaded deviceData — that blocked unlinking.
        let submitItem = {};
        submitItem.StallholderId =
          this.selectedStallholder?.StallholderId || null;
        submitItem.StallId = this.selectedStall?.StallId || null;
        submitItem.TerminalId =
          this.selectedTerminal?.TerminalId || null;
        submitItem.UserId = this.selectedUser?.TerminalUserId || null;
        submitItem.ClearAssignment = !submitItem.TerminalId;
        submitItem.DeviceId = this.eventItem.DeviceId;
        submitItem.MerchantId = this.eventItem.MerchantId;
        submitItem.Authorised =
          this.eventItem.Authorised ||
          (this.deviceData && this.deviceData.Authorised) ||
          "Yes";
        submitItem.EventId = this.globalEventFilter.EventId;
        if (this.Action === "update" && this.originalDeviceId) {
          submitItem.OriginalDeviceId = this.originalDeviceId;
        }

        devicesToSubmit = [submitItem];
      }

      // Validate that we have devices to submit
      if (devicesToSubmit.length === 0) {
        iziToast.warning({
          title: "Device",
          message: "No devices to save!",
          position: "topRight",
        });
        return;
      }

      // Validate form if using single device form
      if (this.importedDevices.length === 0 && this.$refs.form) {
        if (!this.$refs.form.validate()) {
          return;
        }
      }

      this.submitDevices(devicesToSubmit);
      this.close();
    },

    clickCancel: function () {
      this.$router.push({ name: "EventManagement" });
    },

    redirectReload() {
      // Refresh the devices list and go back to EventManagement
      this.$router.push({ name: "EventManagement" });
    },

    downloadTemplate() {
      // Generate template with real data from the system
      let templateRows = [
        "DeviceID,CAIC,Authorised,Stallholder,Stall,Terminal,User",
      ];

      // Generate one device entry per terminal (not per user)
      this.allStallHolders.forEach((stallholder) => {
        if (stallholder.Stalls && stallholder.Stalls.length > 0) {
          stallholder.Stalls.forEach((stall) => {
            if (stall.Terminals && stall.Terminals.length > 0) {
              stall.Terminals.forEach((terminal) => {
                // Create one row per terminal with blank User column
                const row = [
                  "", // Blank DeviceId for user to fill in
                  "", // Blank MerchantId for user to fill in
                  "Yes", // Default to authorised
                  stallholder.StallholderName,
                  stall.StallName,
                  terminal.TerminalNumber,
                  "", // Blank User column
                ].join(",");

                templateRows.push(row);
              });
            } else {
              // If no terminals, create a template row with empty terminal and user
              const row = [
                "", // Blank DeviceId for user to fill in
                "", // Blank MerchantId for user to fill in
                "Yes",
                stallholder.StallholderName,
                stall.StallName,
                "", // Empty terminal
                "", // Empty user
              ].join(",");

              templateRows.push(row);
            }
          });
        } else {
          // If no stalls, create a template row with empty stall, terminal, and user
          const row = [
            "", // Blank DeviceId for user to fill in
            "", // Blank MerchantId for user to fill in
            "Yes",
            stallholder.StallholderName,
            "", // Empty stall
            "", // Empty terminal
            "", // Empty user
          ].join(",");

          templateRows.push(row);
        }
      });

      const templateContent = templateRows.join("\n");

      const blob = new Blob([templateContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", "device-import-template.csv");
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    saveAll() {
      if (!this.canManageDevices) {
        iziToast.warning({
          title: "Access Denied",
          message:
            "You do not have permission to add or edit devices for this event.",
          position: "topRight",
        });
        return;
      }

      const validDevices = this.importedDevices.filter(
        (device) =>
          device.DeviceId && device.MerchantId && device.hasValidMerchantId
      );

      const invalidDevicesCount = this.importedDevices.filter(
        (device) =>
          !device.DeviceId || !device.MerchantId || !device.hasValidMerchantId
      ).length;

      if (validDevices.length === 0) {
        iziToast.warning({
          title: "Device",
          message: "No valid devices to save!",
          position: "topRight",
        });
        return;
      }

      if (invalidDevicesCount > 0) {
        iziToast.error({
          title: "Device",
          message: `Cannot save! ${invalidDevicesCount} devices have invalid data. Please fix the invalid devices first.`,
          position: "topRight",
        });
        return;
      }

      // Show warning about mapping issues if any exist
      if (this.mappingWarningsCount > 0) {
        iziToast.warning({
          title: "Device",
          message: `${this.mappingWarningsCount} devices have mapping warnings but will be saved anyway. Continue?`,
          position: "topRight",
          timeout: 5000,
          buttons: [
            [
              "<button>Continue</button>",
              (instance, toast) => {
                instance.hide(toast, {});
                this.submitDevices(validDevices);
              },
            ],
            [
              "<button>Cancel</button>",
              (instance, toast) => {
                instance.hide(toast, {});
              },
            ],
          ],
        });
      } else {
        // No warnings, save directly
        this.submitDevices(validDevices);
      }
    },

    submitDevices(devicesToSubmit) {
      if (!this.canManageDevices) return;

      const formattedDevices = devicesToSubmit.map((device) => {
        const row = {
          DeviceId: device.DeviceId,
          MerchantId: device.MerchantId,
          Authorised: device.Authorised || "Yes",
          StallholderId: device.StallholderId,
          StallId: device.StallId,
          TerminalId: device.TerminalId,
          UserId: device.UserId,
          EventId: this.globalEventFilter.EventId,
        };
        if (this.Action === "update" && this.originalDeviceId) {
          row.OriginalDeviceId = this.originalDeviceId;
        }
        return row;
      });

      try {
        let eventSubmitItem = {
          Type: "Device",
          EventId: this.globalEventFilter.EventId,
          Action: this.Action,
          Devices: formattedDevices,
        };

        this.postDevices(eventSubmitItem)
          .then((response) => {
            iziToast.success({
              title: "Device",
              message: `Successfully ${
                this.Action === "create" ? "inserted" : "updated"
              } ${formattedDevices.length} device(s)!`,
              position: "topRight",
            });

            console.log("submitDevices() Response", response);
            this.redirectReload();
          })
          .catch((err) => {
            console.log("ERROR: ", err);

            const errors =
              err?.response?.data?.Errors ||
              err?.response?.data?.errors ||
              [];
            const detail = Array.isArray(errors)
              ? errors.filter(Boolean).join("; ")
              : "";
            const message =
              detail ||
              err?.response?.data?.Message ||
              err?.message ||
              "Failed to save devices.";

            iziToast.warning({
              title: "Device",
              message,
              position: "center",
            });
          });
      } catch (e) {
        console.log("ERROR: ", e);
      }
    },
  },
};
</script>
